import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMissionBuilder } from '../MissionBuilderContext';
import { BlockCard } from './BlockCard';

export function FlowCanvas() {
  const { blocks, selectedBlockId, selectBlock, removeBlock, reorderBlocks } = useMissionBuilder();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderBlocks(oldIndex, newIndex);
      }
    }
  };

  const hasTrigger = blocks.some((b) => b.category === 'trigger');
  const hasMission = blocks.some((b) => b.category === 'mission');
  const hasStep = blocks.some((b) => b.category === 'step');

  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        overflow: 'auto',
        bgcolor: 'action.hover',
        borderRadius: 2,
        minHeight: 300,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Mission Flow
      </Typography>

      {blocks.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography color="text.secondary" align="center">
            Click blocks from the palette to build your mission flow.
            <br />
            Need: 1 Trigger + 1 Mission Type + at least 1 Step.
          </Typography>
        </Box>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {blocks.map((block, index) => (
                <React.Fragment key={block.id}>
                  {index > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25 }}>
                      <Box sx={{ width: 2, height: 16, bgcolor: 'divider', borderRadius: 1 }} />
                    </Box>
                  )}
                  <BlockCard
                    block={block}
                    isSelected={selectedBlockId === block.id}
                    onSelect={() => selectBlock(block.id)}
                    onRemove={() => removeBlock(block.id)}
                  />
                </React.Fragment>
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}

      {/* Validation hints */}
      {blocks.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!hasTrigger && (
            <Typography variant="caption" color="warning.main">Missing trigger block</Typography>
          )}
          {!hasMission && (
            <Typography variant="caption" color="warning.main">Missing mission type block</Typography>
          )}
          {!hasStep && (
            <Typography variant="caption" color="warning.main">Need at least 1 step</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
